import { create } from 'zustand';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { FotoPerfil } from '@/types/media';
import { AppConfig } from "@/datasources/AppConfig";

export interface AgenteOuvidoria {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  fotoPerfil?: FotoPerfil;
  intergracao?: string;
  arquivado: boolean;
  papel?: string;
  status?: string;
  gestor?: boolean;
  dataIngresso?: string;
  solicitacoesTratadas?: number;
  slaMedia?: number;
  avatar?: string;
}

interface AgentesState {
  agentes: AgenteOuvidoria[];
  isLoading: boolean;
  error: string | null;
  // Pagination state
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  // Contadores totais (independentes do filtro aplicado na lista).
  totalAprovados: number;
  totalAguardandoAprovacao: number;
  totalReprovados: number;
  isLoadingCounts: boolean;
  // Mapa agenteId -> total de solicitações atribuídas a ele.
  solicitacoesPorAgente: Record<string, number>;
  isLoadingSolicitacoesPorAgente: boolean;
  fetchAgentes: (status?: string, pageNumber?: number, reset?: boolean) => Promise<void>;
  fetchAgentesCounts: () => Promise<void>;
  fetchSolicitacoesPorAgente: (agenteIds: string[]) => Promise<void>;
  resetPagination: () => void;
}

const api = new RemoteDataSource(Settings.urlBase);

type AgenteApi = Record<string, unknown>;

export const useAgentesStore = create<AgentesState>((set, get) => ({
  agentes: [],
  isLoading: false,
  error: null,
  pageNumber: 1,
  pageSize: 10,
  hasMore: true,
  isLoadingMore: false,
  totalAprovados: 0,
  totalAguardandoAprovacao: 0,
  totalReprovados: 0,
  isLoadingCounts: false,
  solicitacoesPorAgente: {},
  isLoadingSolicitacoesPorAgente: false,
  fetchAgentes: async (status?: string, pageNumber = 1, reset = false) => {
    const { pageSize, agentes: existingAgentes } = get();
    
    if (reset) {
      set({ isLoading: true, pageNumber: 1, agentes: [], error: null });
    } else if (pageNumber === 1) {
      set({ isLoading: true, error: null });
    } else {
      set({ isLoadingMore: true, error: null });
    }
    
    try {
      const queryParams: Record<string, unknown> = {};
      if (status && status !== 'all') {
        queryParams.status = status;
      }
      queryParams.PageNumber = pageNumber;
      queryParams.PageSize = pageSize;

      const result = await api.get<AgenteApi[]>({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes`,
        requiredToken: true,
        headers: { accept: 'application/vnd.data.compact.v1+json' },
        forceRefresh: true,
        queryParams,
        serialize: (data) => (Array.isArray(data) ? (data as AgenteApi[]) : []),
      });

      if ('failure' in result) {
        set({ 
          isLoading: false, 
          isLoadingMore: false,
          error: result.failure.messages?.join(', ') || 'Erro ao carregar agentes' 
        });
        return;
      }

      const raw = Array.isArray(result.success.data) ? result.success.data : [];
      const mapped = Array.isArray(raw)
        ? raw.map((item) => {
            const a = item as Record<string, unknown>;
            return {
              id: (a.id ?? a['id'] ?? a['id'] ?? crypto.randomUUID()).toString(),
              nome: (a.nome || a['nome'] || a['nome'] || 'Sem nome') as string,
              email: (a.email || a['email'] || a['mail']) as string | undefined,
              telefone: (a.telefone || a['telefone'] || a['contatoTelefone']) as string | undefined,
              fotoPerfil: (a.fotoPerfil as Record<string, unknown>) ? {
                id: ((a.fotoPerfil as Record<string, unknown>).id as string) || '',
                arquivo: ((a.fotoPerfil as Record<string, unknown>).arquivo as string) || '',
              } : undefined,
              intergracao: (a.intergracao || a['intergracao']) as string | undefined,
              arquivado: (a.arquivado as boolean) || false,
              status: (a.status || a['status']) as string | undefined,
              papel: (a.papel || a['papel']) as string | undefined,
              gestor: (a.gestor as boolean) || false,
              avatar: (a['iniciais'] || a['avatar']) as string | undefined,
            } satisfies AgenteOuvidoria;
          })
        : [];

      const hasMore = mapped.length === pageSize;
      
      if (reset || pageNumber === 1) {
        set({ 
          agentes: mapped, 
          pageNumber, 
          hasMore,
          isLoading: false,
          isLoadingMore: false
        });
      } else {
        set({ 
          agentes: [...existingAgentes, ...mapped], 
          pageNumber, 
          hasMore,
          isLoading: false,
          isLoadingMore: false
        });
      }
    } catch (error: unknown) {
      set({ 
        isLoading: false, 
        isLoadingMore: false,
        error: (error as Error).message || 'Erro ao carregar agentes' 
      });
    }
  },

  // Faz três chamadas em paralelo para contar agentes por status (Aprovado, AguardandoAprovacao e Reprovado)
  // Usa Accept: application/vnd.data.minimal.v1+json para receber apenas o mínimo de dados (mais rápido)
  fetchAgentesCounts: async () => {
    set({ isLoadingCounts: true });
    try {
      const [resAprovados, resAguardando, resReprovados] = await Promise.all([
        api.get<AgenteApi[]>({
          endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes`,
          requiredToken: true,
          headers: { accept: 'application/vnd.data.minimal.v1+json' },
          forceRefresh: true,
          queryParams: { status: 'Aprovado' },
          serialize: (data) => (Array.isArray(data) ? (data as AgenteApi[]) : []),
        }),
        api.get<AgenteApi[]>({
          endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes`,
          requiredToken: true,
          headers: { accept: 'application/vnd.data.minimal.v1+json' },
          forceRefresh: true,
          queryParams: { status: 'AguardandoAprovacao' },
          serialize: (data) => (Array.isArray(data) ? (data as AgenteApi[]) : []),
        }),
        api.get<AgenteApi[]>({
          endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes`,
          requiredToken: true,
          headers: { accept: 'application/vnd.data.minimal.v1+json' },
          forceRefresh: true,
          queryParams: { status: 'Reprovado' },
          serialize: (data) => (Array.isArray(data) ? (data as AgenteApi[]) : []),
        }),
      ]);

      const totalAprovados = !('failure' in resAprovados)
        ? (Array.isArray(resAprovados.success.data) ? resAprovados.success.data.length : 0)
        : 0;

      const totalAguardando = !('failure' in resAguardando)
        ? (Array.isArray(resAguardando.success.data) ? resAguardando.success.data.length : 0)
        : 0;

      const totalReprovados = !('failure' in resReprovados)
        ? (Array.isArray(resReprovados.success.data) ? resReprovados.success.data.length : 0)
        : 0;

      set({ totalAprovados, totalAguardandoAprovacao: totalAguardando, totalReprovados, isLoadingCounts: false });
    } catch (error: unknown) {
      set({ isLoadingCounts: false });
    }
  },

  // Busca a quantidade de solicitações atribuídas a cada agente
  // Usa endpoint /solicitacoes com agenteId e ouvidoriaId como query params
  fetchSolicitacoesPorAgente: async (agenteIds: string[]) => {
    if (agenteIds.length === 0) {
      set({ solicitacoesPorAgente: {} });
      return;
    }

    set({ isLoadingSolicitacoesPorAgente: true });
    try {
      const counts: Record<string, number> = {};

      const fetchAllForAgente = async (agenteId: string): Promise<number> => {
        const pageSize = 500;
        const allItems: unknown[] = [];
        let page = 1;

        while (true) {
          const result = await api.get<unknown[]>({
            endpoint: 'solicitacoes',
            requiredToken: true,
            headers: { accept: 'application/vnd.data.minimal.v1+json' },
            queryParams: {
              agenteId,
              ouvidoriaId: AppConfig.ouvidoriaId,
              PageNumber: page,
              PageSize: pageSize,
            },
            forceRefresh: true,
            serialize: (data) => {
              if (Array.isArray(data)) return data;
              if (data && typeof data === 'object' && 'items' in (data as Record<string, unknown>))
                return (data as Record<string, unknown>).items as unknown[];
              if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>))
                return (data as Record<string, unknown>).data as unknown[];
              return [];
            },
          });

          if ('failure' in result) break;

          const rawBody = result.success.body as Record<string, unknown> | undefined;
          const items = result.success.data || [];

          if (items.length === 0) break;
          allItems.push(...items);

          const totalCount =
            typeof rawBody?.totalCount === 'number'
              ? rawBody.totalCount
              : typeof rawBody?.total === 'number'
                ? rawBody.total
                : typeof rawBody?.totalRecords === 'number'
                  ? rawBody.totalRecords
                  : typeof rawBody?.count === 'number'
                    ? rawBody.count
                    : undefined;

          if (typeof totalCount === 'number' && allItems.length >= totalCount) break;
          if (items.length < pageSize) break;

          page++;
        }

        return allItems.length;
      };

      // Faz uma requisição para cada agente (em paralelo)
      await Promise.all(
        agenteIds.map(async (agenteId) => {
          try {
            counts[agenteId] = await fetchAllForAgente(agenteId);
          } catch {
            counts[agenteId] = 0;
          }
        })
      );

      set({ solicitacoesPorAgente: counts, isLoadingSolicitacoesPorAgente: false });
    } catch (error: unknown) {
      set({ isLoadingSolicitacoesPorAgente: false });
    }
  },

  resetPagination: () => {
    set({
      pageNumber: 1,
      hasMore: true,
      isLoadingMore: false,
      agentes: [],
    });
  },
}));
